import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { updateParamValue, usePagePrefs } from "@pepsico/pref-permissions";

import type { OrthoListItem, OrthoPresignedUrlItem } from "api/model";
import { useGetOrthoPresignedUrls, useGetOrthos } from "api/orthos/orthos";
import { ORTHOS_PREF_KEY } from "defs";

import { buildOverviewOverlay } from "./cogOrtho";
import {
  CogOrthoContext,
  type CogOrthoContextValue,
  type CogOrthoSession,
  type LocationOrtho,
} from "./cogOrthoContext";
import {
  createCogOrthoSessionApi,
  createCogOrthoSessionState,
  disposeCogOrthoSessionState,
  type CogOrthoSessionState,
} from "./cogOrthoSession";
import { createOrthoImageOverlay, type OrthoImageOverlay } from "./orthoAssets";
import {
  OVERVIEW_TILE_ID,
  readCachedOrthoOverview,
  writeCachedOrthoImage,
} from "./orthoImageCache";
import {
  ORTHOS_NONE_PREF,
  isPlotMapOrthoVisible,
  isPlotMapOrthomosaicLayerVisible,
} from "./orthoPrefs";
import { isStitchedOrthoStatus } from "./orthoStatus";

interface SessionEntry {
  api: ReturnType<typeof createCogOrthoSessionApi>;
  state: CogOrthoSessionState;
}

const sortOrthosByFlightDateDesc = (orthos: OrthoListItem[]): OrthoListItem[] =>
  [...orthos].sort((left, right) => {
    const byDate = right.flight_date.localeCompare(left.flight_date);
    if (byDate !== 0) {
      return byDate;
    }
    return right.id - left.id;
  });

interface CogOrthoProviderProps {
  children: ReactNode;
}

/**
 * Owns COG display for the current location: the ortho list, per-ortho
 * visibility, presigned URLs, and open sessions (overview + detail API).
 */
const CogOrthoProvider: React.FC<CogOrthoProviderProps> = ({ children }) => {
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();
  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const isOrthomosaicLayerVisible = isPlotMapOrthomosaicLayerVisible(prefs);
  const sessionsRef = useRef(new Map<number, SessionEntry>());
  const [sessions, setSessions] = useState<Record<number, CogOrthoSession>>({});

  const { data: fetchedOrthos = [] } = useGetOrthos(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): OrthoListItem[] => (res.status === 200 ? res.data : []),
      },
    },
  );

  // Kept after probe: React Compiler does not stabilize these derived arrays/Maps
  // across provider re-renders. useMemo keeps query keys, session sync effects,
  // and context consumers from seeing a new identity every update.
  const sortedOrthos = useMemo(
    () => sortOrthosByFlightDateDesc(fetchedOrthos),
    [fetchedOrthos],
  );
  const orthoIds = useMemo(
    () =>
      sortedOrthos
        .filter((ortho) => isStitchedOrthoStatus(ortho.status))
        .map((ortho) => ortho.id),
    [sortedOrthos],
  );
  const displayableOrthoIds = useMemo(
    () =>
      sortedOrthos
        .filter(
          (ortho) =>
            isStitchedOrthoStatus(ortho.status) && ortho.visible_on_map,
        )
        .map((ortho) => ortho.id),
    [sortedOrthos],
  );
  const orthos = useMemo<LocationOrtho[]>(
    () =>
      sortedOrthos.map((ortho) => ({
        ...ortho,
        isVisible:
          isStitchedOrthoStatus(ortho.status) &&
          ortho.visible_on_map &&
          isPlotMapOrthoVisible(prefs, ortho.id, displayableOrthoIds),
      })),
    [displayableOrthoIds, prefs, sortedOrthos],
  );

  const sessionOrthoIds = useMemo(
    () =>
      isOrthomosaicLayerVisible
        ? orthos.filter((ortho) => ortho.isVisible).map((ortho) => ortho.id)
        : [],
    [isOrthomosaicLayerVisible, orthos],
  );

  // Fetch URLs for every stitched location ortho (not only visible). Uploading
  // and processing orthos have no COG yet. Visibility toggles must not change
  // this query set — a reminted URL for a still-open session would be treated
  // as rotation and force a full COG reload.
  const { data: presignedUrls = [], isPending: isPresignedUrlsPending } =
    useGetOrthoPresignedUrls(
      { ortho_id: orthoIds },
      {
        query: {
          enabled: locationUuid !== "" && orthoIds.length > 0,
          select: (res): OrthoPresignedUrlItem[] =>
            res.status === 200 ? res.data : [],
          staleTime: 50 * 60 * 1000,
          refetchInterval: 50 * 60 * 1000,
        },
      },
    );

  const urlByOrthoId = useMemo(() => {
    const next = new Map<number, string>();
    for (const item of presignedUrls) {
      next.set(item.id, item.presigned_url);
    }
    return next;
  }, [presignedUrls]);

  const sessionUrlKey = sessionOrthoIds
    .map((orthoId) => `${orthoId}:${urlByOrthoId.get(orthoId) ?? ""}`)
    .join("|");

  const displayOrtho = useCallback(
    (orthoId: number | null) => {
      if (!isReady) {
        return;
      }
      setSearchParams(
        (prev) =>
          updateParamValue(
            prev,
            ORTHOS_PREF_KEY,
            prefs,
            orthoId == null ? ORTHOS_NONE_PREF : orthoId.toString(),
          ),
        { replace: true, state: { inPageNav: true } },
      );
    },
    [isReady, prefs, setSearchParams],
  );

  // Keep open COG sessions in sync with the visible ortho set. Ownership is
  // the `state` object in sessionsRef: same id+URL leaves the in-flight load
  // alone, and that load publishes if its `state` is still the ref entry.
  // disposeSession or a URL rotation replaces `state`; unmount disposes leftovers.
  useEffect(() => {
    // Ids that should keep a session: parent Orthomosaic layer on + individual on.
    const wantedIds = new Set(sessionOrthoIds);

    const disposeSession = (orthoId: number): void => {
      const entry = sessionsRef.current.get(orthoId);
      if (entry == null) {
        return;
      }
      // Revoke in-memory blob URLs. IndexedDB keeps the PNG bytes.
      disposeCogOrthoSessionState(entry.state);
      sessionsRef.current.delete(orthoId);
      setSessions((current) => {
        if (!(orthoId in current)) {
          return current;
        }
        const next = { ...current };
        delete next[orthoId];
        return next;
      });
    };

    const loadSession = async (orthoId: number, presignedUrl: string) => {
      const existing = sessionsRef.current.get(orthoId);
      // Same id + same URL: this run did not replace `state`. Skip a second
      // fromUrl; the load that created `existing.state` still owns publish.
      if (existing != null && existing.state.url === presignedUrl) {
        return;
      }

      // URL rotated (presigned refresh) or first open after a prior dispose.
      // Replacing `state` makes the old load's isCurrentSession() fail.
      if (existing != null) {
        disposeCogOrthoSessionState(existing.state);
        sessionsRef.current.delete(orthoId);
      }

      const state = createCogOrthoSessionState();
      state.orthoId = orthoId;
      state.url = presignedUrl;
      const api = createCogOrthoSessionApi(() => {
        const entry = sessionsRef.current.get(orthoId);
        if (entry == null) {
          throw new Error("CogOrtho is not open");
        }
        return entry.state;
      });
      sessionsRef.current.set(orthoId, { api, state });
      // True while this `state` instance is still the ref entry for orthoId.
      const isCurrentSession = (): boolean =>
        sessionsRef.current.get(orthoId)?.state === state;
      // Publish pending before fromUrl so the map can mount a placeholder row.
      setSessions((current) => ({
        ...current,
        [orthoId]: {
          api: null,
          error: null,
          isPending: true,
          overlay: null,
          sessionKey: presignedUrl,
        },
      }));

      const publishOverview = (overlay: OrthoImageOverlay): void => {
        setSessions((current) => ({
          ...current,
          [orthoId]: {
            api,
            error: null,
            isPending: false,
            overlay,
            sessionKey: presignedUrl,
          },
        }));
      };

      try {
        const cachedOverview = await readCachedOrthoOverview(orthoId);
        if (!isCurrentSession()) {
          return;
        }
        if (cachedOverview != null) {
          const imageUrl = URL.createObjectURL(cachedOverview.image.blob);
          if (!isCurrentSession()) {
            URL.revokeObjectURL(imageUrl);
            return;
          }
          const overlay = createOrthoImageOverlay(
            cachedOverview.image.coordinates,
            imageUrl,
          );
          state.overview = overlay;
          state.overviewBlobBytes = cachedOverview.image.byteLength;
          state.transform = cachedOverview.transform;
          console.debug(
            "[CogOrthoProvider] overview cache hit",
            JSON.stringify({
              byteLength: cachedOverview.image.byteLength,
              orthoId,
            }),
          );
          publishOverview(overlay);
          return;
        }

        await api.open();
        // Stale if disposeSession or a URL rotation swapped `state` out.
        if (!isCurrentSession()) {
          return;
        }
        if (state.tiff == null || state.transform == null) {
          throw new Error("CogOrtho is not open");
        }
        const transform = state.transform;
        const {
          blob,
          byteLength,
          height,
          overlay: nextOverlay,
          width,
        } = await buildOverviewOverlay(state.tiff, transform);
        if (!isCurrentSession()) {
          URL.revokeObjectURL(nextOverlay.imageUrl);
          return;
        }
        state.overview = nextOverlay;
        state.overviewBlobBytes = byteLength;
        void writeCachedOrthoImage({
          blob,
          coordinates: nextOverlay.coordinates,
          height,
          kind: "overview",
          orthoId,
          tileId: OVERVIEW_TILE_ID,
          transform,
          width,
        });
        publishOverview(nextOverlay);
      } catch (error) {
        if (!isCurrentSession()) {
          return;
        }
        const nextError =
          error instanceof Error ? error : new Error(String(error));
        console.error(
          "[CogOrthoProvider] Failed to load COG overview:",
          JSON.stringify({
            message: nextError.message,
            name: nextError.name,
            orthoId,
            stack: nextError.stack,
          }),
        );
        setSessions((current) => ({
          ...current,
          [orthoId]: {
            api: null,
            error: nextError,
            isPending: false,
            overlay: null,
            sessionKey: presignedUrl,
          },
        }));
      }
    };

    // Drop sessions that left the visible set (toggled off, location change).
    for (const orthoId of [...sessionsRef.current.keys()]) {
      if (!wantedIds.has(orthoId)) {
        disposeSession(orthoId);
      }
    }

    const loadWanted = async () => {
      // Visible ids exist but `/urls` has not returned yet — don't treat as missing.
      if (sessionOrthoIds.length > 0 && isPresignedUrlsPending) {
        return;
      }
      for (const orthoId of sessionOrthoIds) {
        const presignedUrl = urlByOrthoId.get(orthoId);
        if (presignedUrl == null) {
          // API omits unknown ids; tear down a stale session if we had one.
          disposeSession(orthoId);
          continue;
        }
        await loadSession(orthoId, presignedUrl);
      }
    };

    void loadWanted();
  }, [isPresignedUrlsPending, sessionOrthoIds, sessionUrlKey, urlByOrthoId]);

  // Provider unmount only: revoke every remaining session. Capture the Map
  // now so cleanup uses the same object the rest of the provider mutates.
  useEffect(() => {
    const sessionEntries = sessionsRef.current;
    return () => {
      for (const entry of sessionEntries.values()) {
        disposeCogOrthoSessionState(entry.state);
      }
      sessionEntries.clear();
    };
  }, []);

  // Kept after probe: React Compiler did not stabilize this context value
  // across re-renders; without useMemo, identity churns every commit (often
  // with unchanged contents) and re-renders all useCogOrtho() consumers.
  const value = useMemo<CogOrthoContextValue>(
    () => ({
      orthos,
      presignedUrls: urlByOrthoId,
      sessions,
      displayOrtho,
    }),
    [orthos, urlByOrthoId, sessions, displayOrtho],
  );

  return <CogOrthoContext value={value}>{children}</CogOrthoContext>;
};

export default CogOrthoProvider;
